import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, Users, Copy, Check, Trash2, Download, Shield, Building2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { teamsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

/**
 * Teams page (Org Dashboard).
 *
 * Two states:
 *   1. User has no org yet → show "Create Team" CTA (Stripe checkout flow).
 *   2. User has an org as owner → full dashboard (members, invites, compliance).
 *      User has an org as member → read-only members table.
 *
 * Also handles the post-Stripe return: when `?checkout={session_id}` is in
 * the URL, we poll /teams/payment-confirm/{session_id} until paid.
 */

const Teams = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [team, setTeam] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orgName, setOrgName] = useState('');
  const [seats, setSeats] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [latestInvite, setLatestInvite] = useState(null);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await teamsApi.me();
      setTeam(r.org);
      setIsOwner(!!r.is_owner);
    } catch (err) {
      console.debug('[teams] me() failed:', err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    refresh();
  }, [isAuthenticated, refresh]);

  // Post-Stripe checkout poll
  useEffect(() => {
    const sid = searchParams.get('checkout');
    if (!sid || !isAuthenticated) return;
    setConfirming(true);
    const tick = async (attempt = 0) => {
      try {
        const r = await teamsApi.paymentConfirm(sid);
        if (r.paid) {
          toast.success('Payment confirmed — your team is live!');
          searchParams.delete('checkout');
          searchParams.delete('org');
          setSearchParams(searchParams, { replace: true });
          await refresh();
          setConfirming(false);
          return;
        }
      } catch (err) {
        console.debug('[teams] confirm err:', err?.message);
      }
      if (attempt < 10) {
        setTimeout(() => tick(attempt + 1), 2000);
      } else {
        toast.error('Could not confirm payment — refresh in a minute.');
        setConfirming(false);
      }
    };
    tick();
  }, [searchParams, setSearchParams, isAuthenticated, refresh]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <Building2 className="w-16 h-16 text-[#00FF00] mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-3">Sign in to manage your team</h1>
          <p className="text-gray-400 mb-6">Teams is gated to authenticated users.</p>
          <Button onClick={() => navigate('/')} className="bg-[#00FF00] text-black hover:bg-[#00DD00]">
            Go Home
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" />
      </div>
    );
  }

  // -------- Create-team form (no org yet) ----------
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!orgName.trim() || seats < 2) {
      toast.error('Provide an org name and at least 2 seats');
      return;
    }
    setSubmitting(true);
    try {
      const r = await teamsApi.create(orgName.trim(), Number(seats));
      window.location.href = r.checkout_url;
    } catch (err) {
      toast.error(err.message || 'Failed to start checkout');
      setSubmitting(false);
    }
  };

  if (!team) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="text-center mb-10">
            <Building2 className="w-14 h-14 text-[#00FF00] mx-auto mb-3" />
            <h1 className="text-4xl font-bold mb-2">Create your team</h1>
            <p className="text-gray-400">$49 / seat / month — full Pro for every member.</p>
          </div>
          <Card className="bg-[#0F0F0F] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Team details</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreate} className="space-y-5" data-testid="teams-create-form">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Organisation name</label>
                  <Input
                    data-testid="teams-create-name"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="Acme Security Team"
                    required
                    minLength={2}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Seats</label>
                  <Input
                    data-testid="teams-create-seats"
                    type="number"
                    min={2}
                    max={200}
                    value={seats}
                    onChange={(e) => setSeats(parseInt(e.target.value, 10) || 0)}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Total today: ${(seats * 49).toLocaleString()} (1 month).
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="teams-create-submit"
                  className="w-full bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue to checkout'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  // -------- Org dashboard (member or owner) ----------
  const handleInvite = async () => {
    try {
      const r = await teamsApi.invite();
      setLatestInvite(r);
      toast.success('Invite link generated — share away.');
    } catch (err) {
      toast.error(err.message || 'Could not create invite');
    }
  };
  const handleCopy = async () => {
    if (!latestInvite?.invite_url) return;
    try {
      await navigator.clipboard.writeText(latestInvite.invite_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.debug('[teams] clipboard failed:', err?.message);
    }
  };
  const handleRemove = async (uid) => {
    if (!confirm('Remove this member? Their seat will be freed.')) return;
    try {
      await teamsApi.removeMember(uid);
      toast.success('Member removed');
      refresh();
    } catch (err) {
      toast.error(err.message || 'Remove failed');
    }
  };
  const downloadCompliance = () => {
    // Use a fetch + blob round-trip so the auth cookie flows.
    fetch(teamsApi.complianceCsvUrl(), { credentials: 'include' })
      .then((r) => {
        if (!r.ok) throw new Error('compliance fetch failed');
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cyberinsti-compliance-${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((err) => toast.error(err.message));
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      {confirming && (
        <div
          data-testid="teams-confirming-banner"
          className="bg-[#00FF00]/10 border-b border-[#00FF00]/30 text-[#00FF00] py-2 text-center text-sm"
        >
          <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
          Confirming your payment…
        </div>
      )}
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-sm text-gray-500 uppercase tracking-wide">Organisation</p>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Building2 className="w-8 h-8 text-[#00FF00]" />
              <span data-testid="teams-org-name">{team.name}</span>
              <Badge
                variant="outline"
                className={
                  team.status === 'active'
                    ? 'border-[#00FF00] text-[#00FF00]'
                    : 'border-[#FF9500] text-[#FF9500]'
                }
                data-testid="teams-org-status"
              >
                {team.status}
              </Badge>
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              {team.seats_used} / {team.seats_total} seats used
              {team.expires_at && ` · renews ${new Date(team.expires_at).toLocaleDateString()}`}
            </p>
          </div>
          {isOwner && (
            <div className="flex gap-3">
              <Button
                onClick={downloadCompliance}
                data-testid="teams-download-compliance"
                variant="outline"
                className="border-gray-700 hover:bg-gray-900"
              >
                <Download className="w-4 h-4 mr-2" />
                Compliance CSV
              </Button>
            </div>
          )}
        </div>

        {isOwner && (
          <Card className="bg-[#0F0F0F] border-gray-800 mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg text-white">
                <Shield className="w-5 h-5 text-[#00FF00]" />
                Invite members
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button
                  data-testid="teams-generate-invite"
                  onClick={handleInvite}
                  disabled={team.seats_used >= team.seats_total}
                  className="bg-[#00FF00] text-black hover:bg-[#00DD00]"
                >
                  Generate invite link
                </Button>
                {team.seats_used >= team.seats_total && (
                  <span className="text-sm text-[#FF9500] self-center">
                    All seats taken — remove someone or add more seats.
                  </span>
                )}
              </div>
              {latestInvite?.invite_url && (
                <div
                  className="mt-4 flex items-center gap-2 p-3 bg-[#070707] border border-gray-800 rounded"
                  data-testid="teams-latest-invite"
                >
                  <code className="flex-1 text-sm text-[#00BFFF] truncate">{latestInvite.invite_url}</code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleCopy}
                    data-testid="teams-copy-invite"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="bg-[#0F0F0F] border-gray-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg text-white">
              <Users className="w-5 h-5 text-[#00FF00]" />
              Members ({team.members.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="teams-members-table">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-800">
                    <th className="py-2 pr-4">Member</th>
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Role</th>
                    <th className="py-2 pr-4">Joined</th>
                    {isOwner && <th className="py-2"></th>}
                  </tr>
                </thead>
                <tbody>
                  {team.members.map((m) => (
                    <tr key={m.user_id} className="border-b border-gray-900">
                      <td className="py-3 pr-4 font-medium">{m.username || '—'}</td>
                      <td className="py-3 pr-4 text-gray-400">{m.email}</td>
                      <td className="py-3 pr-4">
                        <Badge
                          variant="outline"
                          className={
                            m.role === 'owner'
                              ? 'border-[#00FF00] text-[#00FF00]'
                              : 'border-gray-700 text-gray-300'
                          }
                        >
                          {m.role}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4 text-gray-400">
                        {m.joined_at && new Date(m.joined_at).toLocaleDateString()}
                      </td>
                      {isOwner && (
                        <td className="py-3 text-right">
                          {m.role !== 'owner' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRemove(m.user_id)}
                              data-testid={`teams-remove-${m.user_id}`}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default Teams;
